'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId || typeof window === 'undefined' || !window.gtag) return;

    const url = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
    window.gtag('config', measurementId, { page_path: url });
  }, [pathname, searchParams]);
}

export function trackEvent(
  eventName: string,
  eventParams: Record<string, string | number | boolean> = {}
) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

/**
 * Track a HubSpot form submission as a GA4 conversion.
 * Call this after a successful form submit.
 */
export function trackHubSpotFormSubmission(params: {
  service?: string;
  page_path?: string;
  form_location?: string;
}) {
  trackEvent('generate_lead', {
    event_category: 'HubSpot Form',
    event_label: params.service || 'General Enquiry',
    page_path: params.page_path || (typeof window !== 'undefined' ? window.location.pathname : ''),
    form_location: params.form_location || 'inline_cta',
    service: params.service || '',
  });

  // Also fire as a dedicated conversion event for GA4 goal tracking
  trackEvent('hubspot_form_submit', {
    service: params.service || '',
    page_path: params.page_path || (typeof window !== 'undefined' ? window.location.pathname : ''),
    form_location: params.form_location || 'inline_cta',
  });
}

/**
 * Track a service page view - fires when a user lands on a service page.
 * Used to measure the journey: landing -> service page -> form submission.
 */
export function trackServicePageView(params: {
  service_category: string;
  service_name: string;
  page_path?: string;
}) {
  trackEvent('service_page_view', {
    service_category: params.service_category,
    service_name: params.service_name,
    page_path: params.page_path || (typeof window !== 'undefined' ? window.location.pathname : ''),
  });
}
